import { isEmpty, VFlow } from 'bold-ui'
import { RadioOption, resolveName, SimNaoEnum } from 'components/form'
import { SimNaoSwitchButtonField } from 'components/form/field/SimNaoSwitchButtonField'
import { MChatPerguntaEnum } from 'graphql/types.generated'
import { useField } from 'react-final-form'
import { MetaPath } from 'util/metaPath'

import { ConsultaSeguimentoIndicatorHelper } from '../../components/ConsultaSeguimentoIndicatorHelper'
import { ConsultaSeguimentoIndicatorResultado } from '../../components/ConsultaSeguimentoIndicatorResultado'
import { ConsultaSeguimentoIndicatorStart } from '../../components/ConsultaSeguimentoIndicatorStart'
import { ConsultaSeguimentoIndicatorStep } from '../../components/ConsultaSeguimentoIndicatorStep'
import { ConsultaSeguimentoPerguntaHeader } from '../../components/ConsultaSeguimentoPerguntaHeader'
import { ConsultaSeguimentoPerguntaHeaderTitle } from '../../components/ConsultaSeguimentoPerguntaHeaderTitle'
import { PerguntaSimNaoRadioGroupBoxField } from '../../components/PerguntaSimNaoRadioGroupBoxField'
import { PerguntaSimNaoRadioListField } from '../../components/PerguntaSimNaoRadioListField'
import { isPerguntaStatusRespondendo } from '../../consulta-seguimento-aplicacao/utils-consultaSeguimentoAplicacao'
import { useConsultaSeguimentoSetResultado } from '../../hooks/useConsultaSeguimentoSetResultado'
import {
  ConsultaSeguimentoPergunta09Model,
  ConsultaSeguimentoPergunta09TrazPraVoceEnum,
} from './model-consultaSeguimentoPergunta09'
import {
  calculateResultadoPergunta09,
  conditionToCalculatePergunta09,
  hasPergunta09TrazPraVoceAnySim,
  isPergunta09TrazPraVoceAllNao,
} from './utils-consultaSeguimentoPergunta09'

interface ConsultaSeguimentoPergunta09FieldProps {
  name: MetaPath<ConsultaSeguimentoPergunta09Model>
}

export function ConsultaSeguimentoPergunta09Field(props: Readonly<ConsultaSeguimentoPergunta09FieldProps>) {
  const { name } = props

  const {
    input: { value },
  } = useField<ConsultaSeguimentoPergunta09Model>(resolveName(name), {
    subscription: { value: true },
  })

  useConsultaSeguimentoSetResultado<ConsultaSeguimentoPergunta09Model>({
    name,
    conditionToCalculate: conditionToCalculatePergunta09,
    calculateResultado: calculateResultadoPergunta09,
  })

  const hasTrazPraVoceAnySim = hasPergunta09TrazPraVoceAnySim(value)
  const isTrazPraVoceAllNao = isPergunta09TrazPraVoceAllNao(value)

  return (
    <VFlow vSpacing={0}>
      <VFlow>
        {isPerguntaStatusRespondendo(value.status) && (
          <ConsultaSeguimentoPerguntaHeader
            pergunta={MChatPerguntaEnum.PERGUNTA_09}
            title={
              <ConsultaSeguimentoPerguntaHeaderTitle sufix=' traz coisas para mostrar para você ou as segura para que você as veja? Não para conseguir ajuda, mas apenas para compartilhar?' />
            }
            status={value.status}
            field={<SimNaoSwitchButtonField name={name.PERGUNTA_09_HEADER} />}
          />
        )}
        {!isEmpty(value.PERGUNTA_09_HEADER) && (
          <VFlow>
            <VFlow vSpacing={0}>
              <ConsultaSeguimentoIndicatorStart />
              {value.PERGUNTA_09_HEADER === SimNaoEnum.SIM && (
                <ConsultaSeguimentoIndicatorHelper
                  title='Por favor, dê um exemplo de algo que ele pode trazer para mostrar para você ou segurar para que você veja.'
                  subtitle='(Se o genitor não der um dos exemplos abaixo, perguntar cada um deles individualmente.)'
                />
              )}
            </VFlow>
            <VFlow vSpacing={0}>
              <PerguntaSimNaoRadioListField<ConsultaSeguimentoPergunta09TrazPraVoceEnum>
                name={name.PERGUNTA_09_TRAZ_PRA_VOCE}
                nameOutro={name.OUTRO}
                options={PERGUNTA_09_SEU_FILHO_TRAZ_PRA_VOCE_OPTIONS}
                label='Seu filho às vezes traz para você...'
                description='Responda os exemplos até pontuar na Pergunta 9 ou avançar para o próximo fluxo'
                required
              />
              {hasTrazPraVoceAnySim && (
                <VFlow vSpacing={0}>
                  <ConsultaSeguimentoIndicatorStep text='Sim pra qualquer um:' />
                  <PerguntaSimNaoRadioGroupBoxField
                    name={name.PERGUNTA_09_SO_PARA_MOSTRAR}
                    label='Isso é só para mostrar a você e não para pedir ajuda?'
                    required
                  />
                </VFlow>
              )}
            </VFlow>
          </VFlow>
        )}
      </VFlow>
      {value.resultado && (
        <ConsultaSeguimentoIndicatorResultado
          resultado={value.resultado}
          text={{ FALHOU: isTrazPraVoceAllNao ? 'Não para todos:' : undefined }}
        />
      )}
    </VFlow>
  )
}

const PERGUNTA_09_SEU_FILHO_TRAZ_PRA_VOCE_OPTIONS: RadioOption<ConsultaSeguimentoPergunta09TrazPraVoceEnum>[] = [
  {
    label: 'Uma figura ou brinquedo para mostrar para você?',
    value: ConsultaSeguimentoPergunta09TrazPraVoceEnum.FIGURA_OU_BRINQUEDO,
  },
  {
    label: 'Um desenho que ele fez?',
    value: ConsultaSeguimentoPergunta09TrazPraVoceEnum.DESENHO_QUE_ELE_FEZ,
  },
  {
    label: 'Uma flor que ele pegou?',
    value: ConsultaSeguimentoPergunta09TrazPraVoceEnum.FLOR_QUE_ELE_PEGOU,
  },
  {
    label: 'Um bichinho que ele encontrou no chão?',
    value: ConsultaSeguimentoPergunta09TrazPraVoceEnum.BICHINHO_QUE_ELE_ENCONTROU,
  },
  {
    label: 'Algumas peças que ele juntou?',
    value: ConsultaSeguimentoPergunta09TrazPraVoceEnum.PECAS_QUE_ELE_JUNTOU,
  },
]
